import {
  ACCENT_IDS,
  CANDIDATE_BEHAVIORS,
  CANDIDATE_PROFILE_IDS,
  DIFFICULTIES,
  DURATIONS,
  INTERVIEW_LANGUAGES,
  MATERIAL_KINDS,
  MAX_MATERIAL_CHARS,
  MAX_MATERIALS,
  PERSONA_IDS,
  SCENARIO_IDS,
  VOICES,
  VOICE_ENGINES,
  type InterviewSetup,
  type MaterialDoc,
} from '../shared/contract';
import { DEFAULT_SETUP, LIVE_VOICES } from './types';

export const STORAGE_KEY = 'mock-interview:setup:v2';
/** v3: earlier builds forced voiceEngine to realtime-2.1; reset it to the default once. */
const SCHEMA_VERSION = 4;
/** v4: the official Tania Min CV became the default AI candidate. */

function pick<T extends string | number>(allowed: ReadonlyArray<T>, value: unknown, fallback: T): T {
  return allowed.includes(value as T) ? (value as T) : fallback;
}

function parseMaterials(value: unknown): MaterialDoc[] {
  if (!Array.isArray(value)) return [];
  const docs: MaterialDoc[] = [];
  for (const raw of value) {
    if (!raw || typeof raw !== 'object') continue;
    const item = raw as Record<string, unknown>;
    if (typeof item.id !== 'string' || typeof item.name !== 'string' || typeof item.text !== 'string') continue;
    const doc: MaterialDoc & { truncated?: boolean } = {
      id: item.id,
      kind: pick(MATERIAL_KINDS, item.kind, 'other'),
      name: item.name.slice(0, 200),
      text: item.text.slice(0, MAX_MATERIAL_CHARS),
    };
    if (item.truncated === true) doc.truncated = true;
    docs.push(doc);
  }
  return docs.slice(0, MAX_MATERIALS);
}

/** Merges whatever is in storage over the defaults, discarding anything invalid. */
export function parseSetup(raw: string | null): InterviewSetup {
  if (!raw) return DEFAULT_SETUP;
  try {
    const data = JSON.parse(raw) as Record<string, unknown>;
    const parsed: InterviewSetup = {
      scenario: pick(SCENARIO_IDS, data.scenario, DEFAULT_SETUP.scenario),
      persona: pick(PERSONA_IDS, data.persona, DEFAULT_SETUP.persona),
      candidateBehavior: pick(CANDIDATE_BEHAVIORS, data.candidateBehavior, DEFAULT_SETUP.candidateBehavior),
      candidateProfile:
        typeof data.schemaVersion === 'number' && data.schemaVersion >= SCHEMA_VERSION
          ? pick(CANDIDATE_PROFILE_IDS, data.candidateProfile, DEFAULT_SETUP.candidateProfile)
          : DEFAULT_SETUP.candidateProfile,
      difficulty: pick(DIFFICULTIES, data.difficulty, DEFAULT_SETUP.difficulty),
      durationMinutes: pick(DURATIONS, data.durationMinutes, DEFAULT_SETUP.durationMinutes),
      focus: typeof data.focus === 'string' ? data.focus.slice(0, 600) : '',
      language: pick(INTERVIEW_LANGUAGES, data.language, DEFAULT_SETUP.language),
      accent: pick(ACCENT_IDS, data.accent, DEFAULT_SETUP.accent),
      role: typeof data.role === 'string' ? data.role.slice(0, 80) : DEFAULT_SETUP.role,
      voice: pick(VOICES, data.voice, DEFAULT_SETUP.voice),
      voiceEngine:
        typeof data.schemaVersion === 'number' && data.schemaVersion >= 3
          ? pick(VOICE_ENGINES, data.voiceEngine, DEFAULT_SETUP.voiceEngine)
          : DEFAULT_SETUP.voiceEngine,
      useDefaultMaterials: typeof data.useDefaultMaterials === 'boolean' ? data.useDefaultMaterials : true,
      materials: parseMaterials(data.materials),
    };
    // Saved non-Live voices would be silently swapped by the server; keep the picker honest.
    if (parsed.voiceEngine === 'gpt-live-1' && !LIVE_VOICES.includes(parsed.voice)) parsed.voice = 'marin';
    return parsed;
  } catch {
    return DEFAULT_SETUP;
  }
}

export function loadSetup(): InterviewSetup {
  try {
    return parseSetup(window.localStorage.getItem(STORAGE_KEY));
  } catch {
    return DEFAULT_SETUP;
  }
}

export function saveSetup(setup: InterviewSetup): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify({ ...setup, schemaVersion: SCHEMA_VERSION }));
  } catch {
    // Storage may be full or unavailable (private mode); persistence is best effort.
  }
}
